import { Button } from "@component/button";
import { Input } from "@component/input";
import { BuildingIcon, PaperclipIcon } from "lucide-react";
import { JobCreateRequest } from "@service/employer";
import { convertCurrency } from "@util/currency";
import { mapJobRateFrequency } from "@constant/app";

interface PreviewJobDetailProps {
    jobData?: JobCreateRequest;
    onApply?: () => void;
}

export default function PreviewJobDetail({ jobData, onApply }: PreviewJobDetailProps) {
    const currency = jobData?.rate_currency || "USD";
    const frequency = mapJobRateFrequency[jobData?.pay_rate_frequency as keyof typeof mapJobRateFrequency] || "";

    const salaryText = (() => {
        const minRate = jobData?.min_rate || 0;
        const maxRate = jobData?.max_rate || 0;
        const fixedRate = jobData?.fixed_rate || 0;

        if ((minRate === 0 || maxRate === 0) && fixedRate === 0) {
            return "Competitive compensation (DOE)";
        }

        if (minRate && maxRate) {
            return `${convertCurrency(minRate, currency)}-${convertCurrency(maxRate, currency)}`;
        }

        return convertCurrency(fixedRate, currency);
    })();

    const hasRate = !((jobData?.min_rate || 0) === 0 && (jobData?.max_rate || 0) === 0 && (jobData?.fixed_rate || 0) === 0);

    return (
        <div className="w-full max-w-5xl 2xl:max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-[1fr,380px] gap-8">
                {/* Left: Main Content */}
                <div className="space-y-6">
                    {/* Header */}
                    <div>
                        <h1 className="text-3xl font-medium text-gray-900 dark:text-white">
                            {jobData?.job_title || "Software Engineer"}
                        </h1>
                        <div className="flex items-center gap-2 mt-2 text-sm text-gray-600 dark:text-gray-400">
                            {jobData?.department && (
                                <>
                                    <span>{jobData.department}</span>
                                    <span className="text-gray-300">&bull;</span>
                                </>
                            )}
                            <span>{jobData?.location || "Remote"}</span>
                        </div>

                        {/* Company Info */}
                        <div className="flex items-center gap-3 mt-4">
                            <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center ring-1 ring-gray-200 dark:ring-gray-700">
                                <BuildingIcon className="w-6 h-6 text-gray-400" />
                            </div>
                            <div>
                                <p className="font-medium text-gray-900 dark:text-white">
                                    {jobData?.company_name || "Company Name"}
                                </p>
                                <p className="text-sm text-gray-500">ourteam.ai</p>
                            </div>
                        </div>
                    </div>

                    {/* Description */}
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                            Description
                        </h2>
                        {jobData?.job_description ? (
                            <div
                                className="text-black dark:text-gray-300 text-sm leading-relaxed prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{ __html: jobData.job_description }}
                            />
                        ) : (
                            <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                                No job description provided yet.
                            </p>
                        )}
                    </div>
                </div>

                {/* Right: Sidebar */}
                <div className="md:sticky md:top-6 self-start">
                    <div className="border rounded-xl p-6 space-y-6 shadow-md">
                        {/* Salary & Job Type */}
                        <div className="space-y-1">
                            <h2 className="text-xl font-bold">
                                {salaryText}
                                {hasRate && frequency && jobData?.pay_rate_frequency !== "One Time" && ` per ${frequency}`}
                            </h2>
                            <p className="text-black">{jobData?.job_type || "Full Time"}{jobData?.work_arrangement ? ` (${jobData.work_arrangement})` : ""}</p>
                        </div>

                        {/* Apply Form (disabled preview) */}
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 gap-4">
                                <Input placeholder="Name*" disabled />
                                <Input placeholder="Email*" disabled />
                                <Input placeholder="Phone number*" disabled />
                            </div>

                            <Input placeholder="LinkedIn (optional)" disabled />

                            {/* CV Upload */}
                            <div className="border border-dashed rounded-md p-4 text-center">
                                <div className="flex flex-col items-center gap-1">
                                    <PaperclipIcon className="h-5 w-5 text-muted-foreground" />
                                    <p className="text-sm font-medium">Upload your CV</p>
                                    <p className="text-xs text-muted-foreground">
                                        Supported format: PDF &amp; DOCX only (max 5MB).
                                    </p>
                                </div>
                            </div>

                            {/* Apply Button */}
                            <Button
                                variant="default"
                                size="lg"
                                className="text-base w-full"
                                onClick={onApply}
                            >
                                Apply Now
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
